import { CheckCircleIcon } from '@heroicons/react/24/outline';

import { PromoTypeV1 } from '@/shared/api/data-contracts';
import { declension, priceFormat } from '@/shared/utils';

import { usePaymentStore } from '../../../store';

interface FullPaymentDetailsProps {
	fullPrice?: number;
	isSubscription: boolean;
	isLongTermSubscription: boolean;
	savedPriceForFullPayment: number | null;
	userSubscriptionIds: string[];
	/** Дней до следующего списания (за 2 дня до конца подписки). Считается по дате окончания текущей подписки + days из API или days − 2 для неавторизованных. */
	daysUntilNextCharge?: number | null;
}

const daysText = (n: number) =>
	`${n} ${declension(n, ['день', 'дня', 'дней'])}`;

/** Выделенный блок: текущие дни премиума сохраняются, подписка продлевается. */
const RemainingDaysNote = () => (
	<div className="flex gap-2 rounded-lg border border-green-light bg-green-light py-2 pl-2 pr-3 text-sm text-gray-2">
		<CheckCircleIcon className="size-5 shrink-0 text-green" />
		<span>
			Оставшиеся дни премиума текущей подписки также будут учтены — продлеваем с
			даты окончания.
		</span>
	</div>
);

export const FullPaymentDetails = ({
	fullPrice,
	userSubscriptionIds,
	isSubscription,
	isLongTermSubscription,
	savedPriceForFullPayment,
	daysUntilNextCharge,
}: FullPaymentDetailsProps) => {
	const { values: formValues } = usePaymentStore();
	const hasOtherSubscription = userSubscriptionIds.length > 0;

	if (isLongTermSubscription && hasOtherSubscription) {
		return (
			<div className="flex flex-col gap-2 pt-2 text-gray-3">
				<p>Следующее списание произойдет за 2 дня до конца подписки.</p>
				<RemainingDaysNote />
				<p>
					Управлять подпиской вы можете в разделе{' '}
					<span className="underline">финансов</span> после оплаты
				</p>
			</div>
		);
	}

	if (isLongTermSubscription) {
		const text =
			daysUntilNextCharge != null
				? `Следующее списание произойдет через ${daysText(daysUntilNextCharge)} (за 2 дня до конца подписки)`
				: 'Следующее списание произойдет за 2 дня до конца подписки';
		return (
			<div className="flex flex-col gap-1 pt-2 text-gray-3">
				<p>{text}</p>
				<p>
					Управлять подпиской вы можете в разделе{' '}
					<span className="underline">финансов</span> после оплаты
				</p>
			</div>
		);
	}

	return (
		<>
			{!!savedPriceForFullPayment && (
				<div className="flex gap-1 text-gray-3">
					<p>Экономия</p>
					<p>
						<span className="font-semibold text-gray-1">
							{priceFormat({ value: savedPriceForFullPayment })}
						</span>
					</p>
				</div>
			)}
			{isSubscription && !formValues.promoCodeValidated && (
				<div className="flex flex-col gap-2 pt-2 text-gray-3">
					<p>
						{daysUntilNextCharge != null
							? `Следующее списание произойдет через ${daysText(daysUntilNextCharge)} (за 2 дня до конца подписки)`
							: 'Следующее списание произойдет за 2 дня до конца подписки'}
					</p>
					{hasOtherSubscription && <RemainingDaysNote />}
					<p>
						Управлять подпиской вы можете в разделе{' '}
						<span className="underline">финансов</span> после оплаты
					</p>
				</div>
			)}

			{formValues.promoCodeValidated &&
				(() => {
					const promoType = formValues.promoCodeType;

					// Неизвестный или отсутствующий тип промокода — не показываем условия.
					if (
						promoType !== PromoTypeV1.ThreeDaysOneRub &&
						promoType !== PromoTypeV1.ThirtyDaysOneRub
					) {
						return (
							<div className="flex flex-col gap-2 pt-2 text-gray-3">
								<p>Промокод недоступен.</p>
							</div>
						);
					}

					const price = fullPrice
						? `${priceFormat({ value: fullPrice })} `
						: undefined;

					return (
						<div className="flex flex-col gap-2 pt-2 text-gray-3">
							{promoType === PromoTypeV1.ThreeDaysOneRub ? (
								<p>
									Первые 3 дня — за 1 ₽. Далее подписка — {price ?? '2500 ₽ '}в
									месяц, списывается автоматически по окончании пробного
									периода.
								</p>
							) : (
								<p>
									Первые 30 дней — за 1 ₽. Далее подписка —{' '}
									{price ?? '2 500 ₽ '}в месяц, первое списание произойдет за 2
									дня до окончания пробного периода.
								</p>
							)}
							{hasOtherSubscription && <RemainingDaysNote />}
							<p>
								Управлять подпиской вы можете в разделе{' '}
								<span className="underline">финансов</span> после оплаты
							</p>
						</div>
					);
				})()}
		</>
	);
};
